import React, { useState, useEffect, useCallback } from 'react'
import { Button, Col, Form, Image, Input, Row, Select, Space, Typography } from 'antd'
import { EditOutlined, CheckOutlined, InboxOutlined } from '@ant-design/icons';
import { useAuthContext } from 'contexts/Auth'
import { useDropzone } from 'react-dropzone';
import { useParams } from 'react-router-dom';
import JoditEditor from 'jodit-react';
import axios from 'axios';

import statuses from 'data/statuses.json'

const { Title, Text } = Typography

const initialState = {
  imageURL: "", bannerURL: "",
  title: "", author: "", slug: "",
  sortOrder: null, status: null,
  description: "",
  blogContent: ""
}

export default function Add() {

  const { getApiConfig } = useAuthContext()
  const [isNew, setIsNew] = useState(true)
  const [state, setState] = useState(initialState)
  const [image, setImage] = useState(null)
  const [isImageVisible, setIsImageVisible] = useState(false)
  const [banner, setBanner] = useState(null)
  const [isBannerVisible, setIsBannerVisible] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isSlugEditable, setIsSlugEditable] = useState(false)

  const params = useParams()

  // console.log('Parms', params)
  // Dropzone
  const { getRootProps: getBannerRootProps, getInputProps: getBannerInputProps } = useDropzone({
    accept: { 'image/*': [] }, multiple: false,
    onDrop: acceptedFiles => { setBanner(acceptedFiles[0]) }
  });
  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'image/*': [] }, multiple: false,
    onDrop: acceptedFiles => { setImage(acceptedFiles[0]) }
  });

  const readBlog = useCallback(async () => {
    if (params.id !== "add") {
      axios.get(`${window.api}/blog/single/${params.id}`, getApiConfig())
        .then((res) => {
          const { data, status } = res
          if (status === 200) {
            const { blog } = data
            setState(s => ({ ...s, ...blog }))
            setIsNew(false)
          }
        })
        .catch(error => {
          console.error(error)
          window.toastify(error.response?.data?.message || "Something went wrong while getting the course", "error")
        })
    } else {
      setState(initialState)
      setIsNew(true)
    }
  }, [params.id, getApiConfig])
  useEffect(() => { readBlog() }, [readBlog])

  const handleChange = e => setState(s => ({ ...s, [e.target.name]: e.target.value }))
  const handleChangeTitle = e => {
    const { value } = e.target
    const title = value
    const slug = window.getSlug(value)
    const abbreviation = window.getAbbreviation(value)
    setState(s => ({ ...s, title, slug, abbreviation }))
  }
  const handleChangeSelect = (name, value) => setState(s => ({ ...s, [name]: value }))


  const handleSubmit = e => {
    e.preventDefault()

    let {
      imageURL, bannerURL, title, slug, author,
      sortOrder, status, description, blogContent
    } = state

    title = title.trim()
    slug = slug.trim()

    if (!title) { return window.toastify('Please enter title', "error") }
    if (!slug) { return window.toastify('Please enter slug', "error") }
    if (!author) { return window.toastify('Please enter author name', "error") }
    if (!sortOrder) { return window.toastify('Please enter sort order', "error") }
    if (!status) { return window.toastify('Please select status', "error") }
    if (!description) { return window.toastify('Please enter description', "error") }

    if (!banner && !bannerURL) { return window.toastify("Please upload banner", "error") }
    if (banner && banner.size > 1048576) { return window.toastify("Banner file size should be less than 1MB", "error") }
    if (!image && !imageURL) { return window.toastify("Please upload course poster", "error") }
    if (image && image.size > 1048576) { return window.toastify("Poster file size should be less than 1MB", "error") }

    let data = {
      imageURL, bannerURL,
      title, slug, author,
      sortOrder, status, description, blogContent,
    }

    const formData = new FormData()
    for (const key in data) { formData.append(key, data[key]); }
    if (image) { formData.append('image', image) }
    if (banner) { formData.append('banner', banner) }

    setIsProcessing(true)
    createDocument(formData)
  }

  const createDocument = async (formData) => {

    axios[isNew ? "post" : "patch"](`${window.api}/blog/${isNew ? "create" : `update/${state.id}`}`, formData, getApiConfig())
      .then((res) => {
        const { data, status } = res
        if (status === isNew ? 201 : 200) {
          window.toastify(data.message, "success")
        }
      })
      .catch(error => {
        console.error(error)
        window.toastify(error.response?.data?.message || `Something went wrong while ${isNew ? "adding a new" : "updating the"} blog`, "error")
      })
      .finally(() => {
        setIsProcessing(false)
      })

  }

  return (
    <>
      <Row className='mb-4'>
        <Col span={24}>
          <div className="flex-between">
            <Title level={3} className="mb-0">Blogs</Title>
            <Button type='primary' loading={isProcessing} onClick={handleSubmit}>{isNew ? "Add" : "Update"}</Button>
          </div>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} lg={18}>
          <div className="card">
            <Row className='mb-4'>
              <Col>
                <Title level={4} className="mb-0">{isNew ? "Add New" : "Update"} Blog</Title>
              </Col>
            </Row>
            <Form layout='vertical'>
              <Row gutter={16}>
                <Col xs={24} lg={12}>
                  <Form.Item label="Title" required>
                    <Input placeholder="Enter title" name="title" value={state.title} onChange={handleChangeTitle} />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={12}>
                  <Form.Item label="Author" required>
                    <Input placeholder="Enter author" name="author" value={state.author} onChange={handleChange} />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={16}>
                  <Form.Item label="Slug" required>
                    <Space.Compact className='w-100'>
                      <Input placeholder="Enter slug" name="slug" value={state.slug} disabled={!isSlugEditable} onChange={handleChange} />
                      <Button onClick={() => { setIsSlugEditable(isEditable => !isEditable) }} icon={!isSlugEditable ? <EditOutlined /> : <CheckOutlined />} />
                    </Space.Compact>
                  </Form.Item>
                </Col>
                <Col xs={24} lg={12}>
                  <Form.Item label="Sort Order" required>
                    <Input placeholder="Enter sort order" name="sortOrder" value={state.sortOrder} onChange={handleChange} onKeyDown={window.onlyNumber} />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={12}>
                  <Form.Item label="Status" required>
                    <Select showSearch placeholder="Select status" value={state.status} onChange={value => handleChangeSelect("status", value)} options={statuses} />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={12}>
                  <Form.Item label="Description" required>
                    <Input placeholder="Enter description" name="description" value={state.description} onChange={handleChange} />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </div>
          {/* <div className="card mt-3">
            <Row>
              <Col span={24}>
                <Title level={4}>Description</Title>
                <JoditEditor value={state.description} onChange={value => handleChangeSelect("description", value)} />
              </Col>
            </Row>
          </div> */}
          <div className="card mt-3">
            <Row>
              <Col span={24}>
                <Title level={4}>Blog Content</Title>
                <JoditEditor value={state.blogContent} onChange={value => handleChangeSelect("blogContent", value)} />
              </Col>
            </Row>
          </div>
          {/* <div className="card mt-3">
            <Row>
              <Col span={24}>
                <Title level={4}>Additional Text</Title>
                <JoditEditor value={state.additionalText} onChange={value => handleChangeSelect("additionalText", value)} />
              </Col>
            </Row>
          </div> */}
        </Col>
        <Col xs={24} lg={6} className='mt-3 mt-lg-0'>

          <div className="card p-2 mb-3">
            <Text className='mb-1'><Text style={{ color: "#ff4d4f" }}>*</Text> <b>Banner</b> (1920 x 700) px</Text>
            <div {...getBannerRootProps({ className: 'dropzone text-center' })}>
              <InboxOutlined />
              <input {...getBannerInputProps()} />
              <p>Click or drag image</p>
            </div>
            {(banner || state.bannerURL) && <Text className='text-primary cursor-pointer text-decoration-underline' onClick={() => { setIsBannerVisible(true) }}>Click to view banner</Text>}
          </div>
          <div className="card p-2">
            <Text className='mb-1'><Text style={{ color: "#ff4d4f" }}>*</Text> <b>Poster</b> (1000 x 1000) px</Text>
            <div {...getRootProps({ className: 'dropzone text-center' })}>
              <InboxOutlined />
              <input {...getInputProps()} />
              <p>Click or drag image</p>
            </div>
            {(image || state.imageURL) && <Text className='text-primary cursor-pointer text-decoration-underline' onClick={() => { setIsImageVisible(true) }}>Click to view image</Text>}
          </div>
        </Col>
      </Row>

      {(image || state.imageURL) && <Image preview={{ visible: isImageVisible, src: image ? URL.createObjectURL(image) : state.imageURL, onVisibleChange: (value) => { setIsImageVisible(value) } }} />}
      {(banner || state.bannerURL) && <Image preview={{ visible: isBannerVisible, src: banner ? URL.createObjectURL(banner) : state.bannerURL, onVisibleChange: (value) => { setIsBannerVisible(value) } }} />}
    </>
  )
}
